/**
 * CooldownMath — pure, deterministic helpers for HUD cooldown visualization.
 *
 * No Babylon or DOM imports. All functions are stateless so they're trivially
 * unit-testable and safe to call from any context (including headless tests).
 */

/**
 * Convert a cooldown progress fraction to a sweep angle in degrees.
 * 0 → 0° (slot is ready — no sweep), 1 → 360° (full cooldown remaining).
 * Input is clamped to [0, 1].
 */
export function progressToSweepAngle(progress01: number): number {
  const clamped = Math.max(0, Math.min(1, progress01));
  return clamped * 360;
}

/**
 * Returns true when a cooldown just crossed from active to ready.
 * Specifically: prev > 0 && curr <= 0.
 */
export function crossedReady(prev: number, curr: number): boolean {
  return prev > 0 && curr <= 0;
}

/**
 * Derive { progress, ready } from raw cooldown state.
 *
 * progress = remaining / duration, clamped to [0, 1].
 *            0 when duration is 0 (guard against div-by-zero).
 * ready    = remaining <= 0 && !isActive
 */
export function cooldownStateForSlot(
  remaining: number,
  duration: number,
  isActive: boolean,
): { progress: number; ready: boolean } {
  const progress =
    duration > 0 ? Math.max(0, Math.min(1, remaining / duration)) : 0;
  const ready = remaining <= 0 && !isActive;
  return { progress, ready };
}

/**
 * Build a data-URI SVG that renders the radial cooldown sweep.
 *
 * The angle is BUCKETED to the nearest 10° step before encoding so that
 * identical angle buckets produce bit-identical strings — this prevents
 * Babylon from thrashing the Image texture on every frame for small
 * floating-point changes.
 *
 * @param angleDeg  Sweep angle in degrees (0 = ready, 360 = full cooldown).
 * @param color     CSS color string for the arc fill.
 * @param size      Slot size in pixels (SVG viewBox is `size × size`).
 * @returns A data:image/svg+xml URI.
 */
export function buildSweepDataUri(angleDeg: number, color: string, size: number): string {
  const bucketed = Math.round(angleDeg / 10) * 10;
  const half = size / 2;
  const r = half * 0.85; // slightly smaller than the slot ring

  if (bucketed <= 0) {
    // Ready: transparent overlay — no arc needed.
    return `data:image/svg+xml,${encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}"><circle cx="${half}" cy="${half}" r="${r}" fill="none"/></svg>`,
    )}`;
  }

  if (bucketed >= 360) {
    // Full cooldown: filled circle.
    return `data:image/svg+xml,${encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}"><circle cx="${half}" cy="${half}" r="${r}" fill="${color}" opacity="0.55"/></svg>`,
    )}`;
  }

  // Arc from top (12 o'clock = -90° in standard math coords) clockwise.
  const rad = (bucketed - 90) * (Math.PI / 180);
  const x = half + r * Math.cos(rad);
  const y = half + r * Math.sin(rad);
  const largeArc = bucketed > 180 ? 1 : 0;
  const d = `M ${half} ${half - r} A ${r} ${r} 0 ${largeArc} 1 ${x} ${y} L ${half} ${half} Z`;
  return `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}"><path d="${d}" fill="${color}" opacity="0.55"/></svg>`,
  )}`;
}
